/**
 * The Tixologi checkout-widget HOST vocabulary: the canonical origin as a value, and the one
 * predicate that answers "is this stored ticket link a checkout-widget link?".
 *
 * WHY THIS LIVES IN `@punchup/utils` AND NOT IN `@punchup/tixologi`, where the rest of the widget
 * machinery lives: its consumers sit BELOW that package in the dependency graph.
 * `extractTixologiEventId` (this package) is consumed by `@punchup/data`
 * (`src/legacy/tour-dates/updaterFunction.ts`), and the venue-page components
 * (`@punchup/venues`) guard their Buy buttons with the predicate — while `@punchup/tixologi`
 * declares `@punchup/data`, `@punchup/utils` AND `@punchup/venues` as dependencies. Any of those
 * reaching back up into it is a dependency CYCLE: turbo refuses the graph outright once the edge
 * is declared honestly, and an undeclared edge only "works" by npm workspace hoisting, invisible
 * to the build graph.
 *
 * So the host vocabulary lives at the leaf, and the domain package consumes it downward:
 * `packages/tixologi/src/widget-origin.ts` re-exports this surface and builds the PREVIEW-swap
 * machinery on top (which origin the iframe actually loads, where postMessage targets). Read that
 * module for the swap side; nothing in THIS file ever learns that previews exist.
 *
 * THE TRAP this module exists to prevent: "is this a Tixologi checkout-widget link?" had FOUR
 * answers across 15 files, and the two most common failed in OPPOSITE directions the moment the
 * environment variable was empty:
 *
 *   link.includes(process.env.NEXT_PUBLIC_TIXOLOGI_URL || "")     `.includes("")` is ALWAYS true,
 *                                                                 so every ticket link on earth
 *                                                                 became a widget link — fails OPEN
 *   link.includes(process.env.NEXT_PUBLIC_TIXOLOGI_URL!)          interpolates the literal string
 *                                                                 "undefined" — fails CLOSED
 *   link.startsWith(process.env.NEXT_PUBLIC_TIXOLOGI_WIDGET_URL!) a third semantic again
 *
 * Some call sites additionally OR in `tixologi_event_id`, some OR in "localhost", some do neither.
 * This module owns exactly the HOST question. The `tixologi_event_id` fallback is an identity
 * signal, not a host signal, so folding it in here would make a row with no link look like a link
 * on the widget's host — surfaces that want the compound "does this show check out through Fast
 * Checkout?" question use `isTixologiCheckoutShow` (`packages/tixologi/src/widget-origin.ts`).
 *
 * Deliberate tightening vs. the substring era: matching is by host SEGMENT (equality, or a
 * subdomain of the configured host), so `https://tixologi.com.attacker.example/event/1` no longer
 * passes a guard configured with `tixologi.com`. Every real widget link keeps passing.
 */

/**
 * The local-dev escape hatch, preserved from the hand-rolled `.includes("localhost")` guards.
 *
 * It is recognised WITHOUT any configuration, because a developer running the widget on
 * localhost:3003 has no canonical host configured to match against in the first place.
 */
export const LOCAL_DEV_HOST = "localhost";

/**
 * Parse a URL-ish operator-supplied value: a full origin, a bare host, or a full link.
 *
 * `NEXT_PUBLIC_TIXOLOGI_URL` ships as the BARE HOST `tixologi.com` (apps/web/.env.local) while
 * `NEXT_PUBLIC_TIXOLOGI_WIDGET_URL` ships as a full origin — both shapes are real, so the retry
 * below is not defensive padding, it is the configured reality.
 *
 * `hostname` is checked because `new URL()` accepts things that are not hosts: `localhost:3003/x`
 * parses happily as protocol `localhost:` with an EMPTY hostname, which would otherwise silently
 * match nothing.
 */
export function parseUrlish(value: string | null | undefined): URL | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return toUrl(trimmed) ?? toUrl(`https://${trimmed}`);
}

function toUrl(candidate: string): URL | null {
  try {
    const url = new URL(candidate);
    return url.hostname ? url : null;
  } catch {
    return null;
  }
}

/**
 * The one home for the host-or-subdomain comparison. Two legacy twins of this idiom exist
 * (`normalizeTixologiTicketLink.ts`, `@punchup/data`'s widget-api-service) — migrate them here
 * opportunistically when touching those files, not in bulk.
 */
export function isHostOrSubdomainOf(host: string, canonical: string): boolean {
  return host === canonical || host.endsWith(`.${canonical}`);
}

/**
 * The origin the Tixologi checkout-widget link builders emit, exactly as configured — or null when
 * the variable is unset or blank.
 *
 * The RAW string, deliberately not a parsed host: the one caller that needs it as a value must not
 * ask the host question. `extractTixologiEventId` tests a link with `startsWith` because it answers
 * a narrower question — "did THIS repo build this link, so is the `/event/<id>/` segment in it ours
 * to read?" — and its answer is written to `shows.tixologi_event_id`. Widening it to the host
 * predicate would widen a WRITE path against the shared staging database.
 *
 * Null rather than `""` is the whole point: `"".startsWith` and `.includes("")` are both true for
 * every string, so a blank variable must be an absent origin, never an empty prefix.
 */
export function canonicalWidgetOrigin(): string | null {
  return process.env.NEXT_PUBLIC_TIXOLOGI_WIDGET_URL?.trim() || null;
}

/**
 * The hosts a checkout-widget link may legitimately live on.
 *
 * BOTH widget variables contribute: `NEXT_PUBLIC_TIXOLOGI_URL` is what the read-side guards test
 * and `NEXT_PUBLIC_TIXOLOGI_WIDGET_URL` is what the write-side link builders emit, so a link
 * written by this repo must be recognised by this repo even when only one of the two is set.
 *
 * The `filter` is the fail-open fix in one line: an unset OR EMPTY variable yields no host and
 * therefore matches nothing, where `.includes(value || "")` matched everything.
 */
function canonicalWidgetHosts(): string[] {
  return [
    parseUrlish(process.env.NEXT_PUBLIC_TIXOLOGI_URL),
    parseUrlish(canonicalWidgetOrigin()),
  ]
    .filter((url): url is URL => url !== null)
    .map((url) => url.hostname.toLowerCase());
}

/**
 * Is this stored ticket link a Tixologi checkout-widget link?
 *
 * The single predicate replacing every hand-rolled host test. TOTAL by construction: a missing,
 * empty or unparseable link is simply not a widget link, and nothing here throws.
 *
 * It tests the link EXACTLY as stored and knows nothing about preview deployments — see the module
 * header: stored links always carry the canonical origin, so a preview must never move this guard.
 */
export function isTixologiWidgetLink(link: string | null | undefined): boolean {
  const host = parseUrlish(link)?.hostname.toLowerCase();
  if (!host) return false;
  if (isHostOrSubdomainOf(host, LOCAL_DEV_HOST)) return true;
  return canonicalWidgetHosts().some((canonical) =>
    isHostOrSubdomainOf(host, canonical)
  );
}
